import { useCallback, useRef, useState } from 'react';

/**
 * THE keep/remove promise-resolve handshake behind every
 * `patchHireWithAdjustmentDecision` call's `onNeedsDecision` (extend /
 * shorten / change-start / extend-all, both single-line and bulk): show the
 * adjustments, and resolve the promise the wrapper is awaiting once a human
 * picks Keep / Remove / Cancel by calling `decide`.
 *
 * Extracted 2026-09-28 from FOUR verbatim copies (`scripts/duplication-manifest.js`
 * group `9b300308`) — `handleAdjustmentDecision` on the three hires pages
 * (`AdminHires.js`, `DepartmentHires.js`, `MyHires.js`) and `decide` in
 * `components/hires/HireOrderExpansion.js`'s `BulkHireHost` (the Extend all /
 * Off hire all / Bring all back host).
 *
 * 🔑 **Only the handshake is shared — a caller whose 409 body needs reshaping
 * before it is shown (extend-all's is PER LINE, `[{ item_id, adjustments }]`,
 * and `HireAdjustmentDecisionDialog` wants the flat rows single-line Extend
 * sends) does that in its OWN wrapper around `onNeedsDecision`, never here**:
 * ```js
 * const { pendingAdjustments, onNeedsDecision, decide } = useAdjustmentDecision();
 * const onNeedsDecisionFlat = (groups) => onNeedsDecision(flattenAdjustments(groups));
 * ```
 *
 * Returns `{ pendingAdjustments, onNeedsDecision, decide }`:
 * - `pendingAdjustments` — `null` when no decision is pending, else the array
 *   to hand `HireAdjustmentDecisionDialog`.
 * - `onNeedsDecision(adjustments)` — pass straight to
 *   `patchHireWithAdjustmentDecision`'s `{ onNeedsDecision }`: shows the
 *   dialog and returns a promise that resolves once `decide` is called.
 * - `decide(decision)` — `HireAdjustmentDecisionDialog`'s `onDecide`: hides
 *   the dialog and resolves the pending promise with `decision`
 *   (`'keep' | 'remove' | null`). A no-op if nothing is pending.
 */
export default function useAdjustmentDecision() {
  const [pendingAdjustments, setPendingAdjustments] = useState(null);
  const resolveRef = useRef(null);

  const onNeedsDecision = useCallback((adjustments) => {
    setPendingAdjustments(adjustments);
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const decide = useCallback((decision) => {
    setPendingAdjustments(null);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    if (resolve) resolve(decision);
  }, []);

  return { pendingAdjustments, onNeedsDecision, decide };
}
